import { Box, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';

import { ScheduleFields, SendRecurrenceFrequency } from 'domains/campaigns/types';
import { getRecurrenceLabel } from 'domains/campaigns/utils';
import Autocomplete, { AutocompleteOption } from 'domains/core/components/Autocomplete';
import DropdownButton from 'domains/core/components/DropdownButton';
import InputLabel from 'domains/core/components/InputLabel';
import InfoSnackbar from 'domains/core/components/Snackbars/InfoSnackbar';
import theme from 'theme';

import { disabledMessage } from './RecommendedTimeSwitch';
import { getRecommendedTime } from './utils/getRecommendedTime';

const dropdownButtonStyles = {
    backgroundColor: theme.palette.action.active,
    textTransform: 'capitalize',
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
};

const WEEKDAY_MAP: Record<number, string> = { 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA', 7: 'SU' };

const OCCURRENCE_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: '1st' },
    { value: 2, label: '2nd' },
    { value: 3, label: '3rd' },
    { value: 4, label: '4th' },
    { value: -1, label: 'Last' },
];

const DAY_OF_WEEK_OPTIONS: { value: string; label: string }[] = [
    { value: 'MO', label: 'Monday' },
    { value: 'TU', label: 'Tuesday' },
    { value: 'WE', label: 'Wednesday' },
    { value: 'TH', label: 'Thursday' },
    { value: 'FR', label: 'Friday' },
    { value: 'SA', label: 'Saturday' },
    { value: 'SU', label: 'Sunday' },
];

export type Props = {
    scheduleFields: ScheduleFields;
    sendDate: DateTime;
    editSchedule: (payload: Partial<ScheduleFields>) => void;
    setEndDate: (payload: DateTime) => void;
    setHasEndDate: (payload: boolean) => void;
    setRecommendedTimeSwitchTouched: (payload: boolean) => void;
    setSendTime: (payload: DateTime) => void;
};

const RecurrenceDropdown = ({
    scheduleFields,
    sendDate,
    editSchedule,
    setEndDate,
    setHasEndDate,
    setRecommendedTimeSwitchTouched,
    setSendTime,
}: Props) => {
    const { monthlyRecurringSends: hasMonthlyRecurringSends } = useFlags();

    const {
        isRecommendedTimeEnabled,
        recurrenceFrequency,
        recurrenceDayOfWeek,
        recurrenceOccurrencePosition,
    } = scheduleFields;

    const handleDoesNotRepeatSelection = () => {
        const recurrence = SendRecurrenceFrequency.DOES_NOT_REPEAT;
        const newDatetime = getRecommendedTime(sendDate, recurrence);

        if (isRecommendedTimeEnabled) {
            setSendTime(newDatetime);
        }

        editSchedule({
            ...scheduleFields,
            recurrenceFrequency: recurrence,
            scheduledEndTimestamp: '',
            recurrenceDayOfWeek: null,
            recurrenceOccurrencePosition: null,
            ...(isRecommendedTimeEnabled && { scheduledTimestamp: newDatetime.toISO() }),
        });

        setHasEndDate(false);
        setEndDate(null);
        setRecommendedTimeSwitchTouched(true);
    };

    const handleContinuousSelection = () => {
        editSchedule({
            ...scheduleFields,
            recurrenceFrequency: SendRecurrenceFrequency.CONTINUOUS,
            isRecommendedTimeEnabled: false,
            recurrenceDayOfWeek: null,
            recurrenceOccurrencePosition: null,
        });

        setRecommendedTimeSwitchTouched(true);
    };

    const handleMonthlySelection = () => {
        const weekday = sendDate.weekday; // 1=Mon...7=Sun
        const dayOfWeek = WEEKDAY_MAP[weekday];

        // Which nth occurrence of this weekday in the month?
        const dayOfMonth = sendDate.day;
        const occurrencePosition =
            dayOfMonth <= 7 ? 1 : dayOfMonth <= 14 ? 2 : dayOfMonth <= 21 ? 3 : dayOfMonth <= 28 ? 4 : -1;

        editSchedule({
            ...scheduleFields,
            recurrenceFrequency: SendRecurrenceFrequency.MONTHLY,
            isRecommendedTimeEnabled: false,
            recurrenceDayOfWeek: dayOfWeek,
            recurrenceOccurrencePosition: occurrencePosition,
        });
        setRecommendedTimeSwitchTouched(true);
    };

    const handleRecurrenceSelection = (recurrence: SendRecurrenceFrequency) => {
        const newDatetime = getRecommendedTime(sendDate, recurrence);

        if (isRecommendedTimeEnabled) {
            setSendTime(newDatetime);
        }

        editSchedule({
            ...scheduleFields,
            recurrenceFrequency: recurrence,
            recurrenceDayOfWeek: null,
            recurrenceOccurrencePosition: null,
            ...(isRecommendedTimeEnabled && { scheduledTimestamp: newDatetime.toISO() }),
        });

        setRecommendedTimeSwitchTouched(true);
    };

    const optionsRecurrenceFrequency = [
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.DOES_NOT_REPEAT).toLowerCase(),
            onClick: handleDoesNotRepeatSelection,
        },
        {
            label: (
                <InputLabel
                    label="Continual"
                    labelIconTooltipText="Continual sends are delivered when the audience criteria is met."
                />
            ),
            onClick: handleContinuousSelection,
        },
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.DAILY).toLowerCase(),
            onClick: () => handleRecurrenceSelection(SendRecurrenceFrequency.DAILY),
        },
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.WEEKLY, sendDate?.toISO()).toLowerCase(),
            onClick: () => handleRecurrenceSelection(SendRecurrenceFrequency.WEEKLY),
        },
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.EVERY_WEEKDAY).toLowerCase(),
            onClick: () => handleRecurrenceSelection(SendRecurrenceFrequency.EVERY_WEEKDAY),
        },
        ...(hasMonthlyRecurringSends
            ? [
                  {
                      label: getRecurrenceLabel(SendRecurrenceFrequency.MONTHLY).toLowerCase(),
                      onClick: handleMonthlySelection,
                  },
              ]
            : []),
    ];

    const handleOccurrenceChange = (_event: React.SyntheticEvent, option: AutocompleteOption<number> | null) => {
        if (option) {
            editSchedule({ ...scheduleFields, recurrenceOccurrencePosition: option.value });
        }
    };

    const handleDayOfWeekChange = (_event: React.SyntheticEvent, option: AutocompleteOption<string> | null) => {
        if (option) {
            editSchedule({ ...scheduleFields, recurrenceDayOfWeek: option.value });
        }
    };

    const selectedOccurrenceOption = OCCURRENCE_OPTIONS.find(({ value }) => value === recurrenceOccurrencePosition);
    const selectedDayOfWeekOption = DAY_OF_WEEK_OPTIONS.find(({ value }) => value === recurrenceDayOfWeek);

    const renderRecommendedTimeSnackbar = () => {
        if (!isRecommendedTimeEnabled || recurrenceFrequency === SendRecurrenceFrequency.DOES_NOT_REPEAT) return;

        if (recurrenceFrequency === SendRecurrenceFrequency.CONTINUOUS) {
            return (
                <InfoSnackbar
                    key="continuous-recurrence-recommended-time-disabled"
                    successMessage={
                        <Typography color="info.content" component="span" variant="body2">
                            {disabledMessage}
                        </Typography>
                    }
                />
            );
        }

        return (
            <InfoSnackbar
                key={`${recurrenceFrequency}-recurrence-recommended-time-updated`}
                successMessage={
                    <Typography color="info.content" component="span" variant="body2">
                        Cured's recommended time updated based on{' '}
                        <Typography color="info.dark" component="span" variant="body2">
                            recurring send
                        </Typography>
                        .
                    </Typography>
                }
            />
        );
    };

    return (
        <>
            {renderRecommendedTimeSnackbar()}
            <Box>
                <DropdownButton
                    sx={dropdownButtonStyles}
                    data-testid="recurrence-button"
                    label={getRecurrenceLabel(
                        recurrenceFrequency,
                        sendDate.toISO(),
                        recurrenceDayOfWeek,
                        recurrenceOccurrencePosition
                    )}
                    options={optionsRecurrenceFrequency}
                    variant="contained"
                    size="small"
                />
                {recurrenceFrequency === SendRecurrenceFrequency.MONTHLY && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Autocomplete
                            data-testid="occurrence-position-select"
                            disableClearable
                            fullWidth={false}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            label="Occurrence"
                            onChange={handleOccurrenceChange}
                            options={OCCURRENCE_OPTIONS}
                            textFieldSx={{ width: '9rem' }}
                            type="number"
                            value={selectedOccurrenceOption ?? null}
                        />
                        <Autocomplete
                            data-testid="day-of-week-select"
                            disableClearable
                            fullWidth={false}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            label="Day"
                            onChange={handleDayOfWeekChange}
                            options={DAY_OF_WEEK_OPTIONS}
                            textFieldSx={{ width: '10rem' }}
                            type="string"
                            value={selectedDayOfWeekOption ?? null}
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default RecurrenceDropdown;
